import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import AppContext from 'contexts/AppContext';
import { useAdminIssues } from 'hooks/useAdminIssues';
import { getJiraIssueUrl } from 'utils/jiraUrls';
import { useMissions } from '../useMissions';
import { assignmentLabel, assignmentState } from './upcomingAssignment';

interface ParentSiblingsLineProps {
  serviceDeskId: number;
  issueKey: string;
  parentKey: string;
}

/**
 * The other children of the open mission's parent, each with its assignment state.
 * The link is symmetric, so rows are kept on their own `parentKey`: the parent and stray links drop.
 */
const SiblingsLine = ({ serviceDeskId, issueKey, parentKey }: ParentSiblingsLineProps) => {
  const { t } = useTranslation();
  const { jiraBrowseBase } = useContext(AppContext);
  const listing = useAdminIssues({ serviceDeskId, parentKey });
  const siblings = useMemo(
    () =>
      (listing.data?.issues ?? []).filter((row) => row.parentKey === parentKey && row.issueKey !== issueKey),
    [listing.data, parentKey, issueKey]
  );
  const { missions, assignmentsKnown } = useMissions(serviceDeskId, siblings);

  if (listing.isLoading || listing.error || missions.length === 0) return null;

  return (
    <div className="flex flex-wrap items-baseline gap-x-2 text-[12.5px] text-muted-foreground">
      <span>{t('{{count}} other tickets in the same request', { count: missions.length })}</span>
      <ul className="flex flex-wrap gap-x-3">
        {missions.map((sibling) => (
          <li key={sibling.issueKey}>
            <a
              href={getJiraIssueUrl(sibling.issueKey, jiraBrowseBase)}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-primary hover:underline"
            >
              {sibling.issueKey}
            </a>
            {/* Unknown assignments show the bare key: a default « À traiter » would be a guess. */}
            {assignmentsKnown && ` (${assignmentLabel(t, assignmentState(sibling.request))})`}
          </li>
        ))}
      </ul>
    </div>
  );
};

const ParentSiblingsLine = (props: ParentSiblingsLineProps) => (
  <Sentry.ErrorBoundary fallback={null}>
    <SiblingsLine {...props} />
  </Sentry.ErrorBoundary>
);

export default ParentSiblingsLine;
